/**
 * Paginated player search for the "Compare vs. Player" submenu (SE-7111).
 *
 * The submenu used to call the search query inline with a hardcoded
 * `first: 10`, read no `pageInfo`, and never call `fetchMore` — so a player the
 * backend ranked eleventh or lower could not be selected however exactly their
 * name was typed. Vicente reproduced it with Juan Fernando Quintero, Felipe
 * Ogaz and Erick Pulgar, all of them unique exact matches ranked below the top
 * ten by GPR.
 *
 * Deliberately NOT routed through `@/hooks/usePlayerSearchPagination`: that
 * hook always sends an explicit `sortBy`/`sortOrder`, and this call site sends
 * neither. Adding them would change which ranking the backend applies to a name
 * query — the exact-match-ranking surface SE-7314 and SE-7581 are about — which
 * is a different decision from paging and not this ticket's to make. The shared
 * `useInfiniteScroll` primitive underneath it is reused as-is.
 * @module features/player-detail/hooks/useComparePlayerSearch
 */

import { useMemo } from "react";

import { usePlayerSearchSearchPlayersLiteQuery } from "@/generated/graphql";

import { COMPARE_PLAYER_SEARCH_PAGE_SIZE } from "@/features/player-detail/constants";
import { transformSearchPlayersToCompareItems } from "@/features/player-detail/utils/performanceAnalyticsUtils";
import type { CompareItem } from "@/features/player-detail/types";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";

/** Shortest query the search will run — below this the backend matches everything. */
const MIN_SEARCH_LENGTH = 2;

/** Paginated results for the compare-vs-player submenu. */
interface UseComparePlayerSearchReturn {
  /** Every player fetched so far, across all pages loaded. */
  readonly playerSearchResults: readonly CompareItem[];
  /**
   * Whether a NEW term is in flight. Excludes a next-page fetch: the submenu
   * renders no rows while this is true, so counting pagination here would
   * blank the very list being paged through.
   */
  readonly playersLoading: boolean;
  /** Whether the connection reports a further page. */
  readonly hasMorePlayers: boolean;
  /** Whether that further page is currently being fetched. */
  readonly isLoadingMorePlayers: boolean;
  /** Fetches the next page from the connection's cursor. */
  readonly loadMorePlayers: () => void;
}

/**
 * Runs the compare-vs-player search as a paginated connection.
 * @param debouncedSearchQuery - Debounced submenu search text
 * @returns Accumulated results plus paging state for the submenu
 */
export const useComparePlayerSearch = (
  debouncedSearchQuery: string
): UseComparePlayerSearchReturn => {
  const {
    data: playersData,
    loading: isPlayerSearchInFlight,
    fetchMore: fetchMorePlayers,
    networkStatus: playerSearchNetworkStatus,
  } = usePlayerSearchSearchPlayersLiteQuery({
    variables: {
      query: debouncedSearchQuery,
      first: COMPARE_PLAYER_SEARCH_PAGE_SIZE,
    },
    skip:
      !debouncedSearchQuery || debouncedSearchQuery.length < MIN_SEARCH_LENGTH,
    fetchPolicy: "cache-and-network",
    // Required for `networkStatus` to report `fetchMore` at all; without it
    // `useInfiniteScroll` can never see a page request in flight.
    notifyOnNetworkStatusChange: true,
  });

  /* `searchPlayers` carries a `relayStylePagination` merge policy keyed on
   * `["query", "sortBy", "sortOrder"]` (`providers/apolloPaginationPolicies.ts`),
   * so each fetched page APPENDS to the same cached connection rather than
   * replacing it — which is what makes the accumulated list below grow. */
  const { loadMore, hasMore, isLoadingMore } = useInfiniteScroll({
    pageInfo: playersData?.searchPlayers?.pageInfo,
    fetchMore: fetchMorePlayers,
    networkStatus: playerSearchNetworkStatus,
  });

  const playerSearchResults = useMemo(
    () =>
      transformSearchPlayersToCompareItems(playersData?.searchPlayers?.edges),
    [playersData?.searchPlayers?.edges]
  );

  return {
    playerSearchResults,
    playersLoading: isPlayerSearchInFlight && !isLoadingMore,
    hasMorePlayers: hasMore,
    isLoadingMorePlayers: isLoadingMore,
    loadMorePlayers: loadMore,
  };
};
